import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  deleteDoc,
  addDoc,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const firebaseService = {
  // Helper: Normalize Firestore data (convert timestamps)
  normalizeData: (data: any): any => {
    if (!data) return data;
    if (Array.isArray(data)) {
      return data.map(item => firebaseService.normalizeData(item));
    }
    if (typeof data === 'object') {
      const normalized: any = {};
      for (const key in data) {
        const value = data[key];
        if (value && typeof value === 'object' && (value.toDate || value.seconds)) {
          normalized[key] = firebaseService.convertTimestamp(value);
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          normalized[key] = firebaseService.normalizeData(value);
        } else {
          normalized[key] = value;
        }
      }
      return normalized;
    }
    return data;
  },

  // Generic get document
  getDocument: async <T>(collectionName: string, docId: string): Promise<T | null> => {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = { id: docSnap.id, ...docSnap.data() };
    return firebaseService.normalizeData(data) as T;
  },

  // Generic get documents with filters
  getDocuments: async <T>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> => {
    const q = query(collection(db, collectionName), ...constraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = { id: doc.id, ...doc.data() };
      return firebaseService.normalizeData(data) as T;
    });
  },

  // Paginated get documents
  getDocumentsPaginated: async <T>(
    collectionName: string,
    page: number = 1,
    pageSize: number = 10,
    constraints: QueryConstraint[] = [],
    sortField: string = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<{ data: T[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
    // Get total count (simplified - in production, consider using a separate count collection)
    const allDocs = await firebaseService.getDocuments<T>(collectionName, constraints);
    const total = allDocs.length;

    // Apply pagination
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    // Apply sorting
    const sortedDocs = [...allDocs].sort((a: any, b: any) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      
      // Convert to Date if needed
      if (aVal) {
        if (aVal?.toDate && typeof aVal.toDate === 'function') {
          aVal = aVal.toDate();
        } else if (!(aVal instanceof Date)) {
          aVal = firebaseService.convertTimestamp(aVal);
        }
      }
      if (bVal) {
        if (bVal?.toDate && typeof bVal.toDate === 'function') {
          bVal = bVal.toDate();
        } else if (!(bVal instanceof Date)) {
          bVal = firebaseService.convertTimestamp(bVal);
        }
      }
      
      // Compare dates
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortOrder === 'desc' ? bVal.getTime() - aVal.getTime() : aVal.getTime() - bVal.getTime();
      }
      
      // Fallback comparison
      if (sortOrder === 'desc') {
        return (bVal || 0) > (aVal || 0) ? 1 : -1;
      }
      return (aVal || 0) > (bVal || 0) ? 1 : -1;
    });

    const paginatedData = sortedDocs.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      pagination: {
        page,
        limit: pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
      },
    };
  },

  // Add document
  addDocument: async (collectionName: string, data: any): Promise<string> => {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  },

  // Add document to subcollection
  addSubDocument: async (collectionName: string, docId: string, subCollectionName: string, data: any): Promise<string> => {
    const subCollectionRef = collection(db, collectionName, docId, subCollectionName);
    const docRef = await addDoc(subCollectionRef, {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  },

  // Get documents from subcollection
  getSubDocuments: async <T>(
    collectionName: string,
    docId: string,
    subCollectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> => {
    const subCollectionRef = collection(db, collectionName, docId, subCollectionName);
    const q = query(subCollectionRef, ...constraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = { id: doc.id, ...doc.data() };
      return firebaseService.normalizeData(data) as T;
    });
  },

  // Update document
  updateDocument: async (collectionName: string, docId: string, data: any): Promise<void> => {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
    });
  },

  // Delete document
  deleteDocument: async (collectionName: string, docId: string): Promise<void> => {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  },

  // Helper: Convert Firestore timestamp to Date
  convertTimestamp: (timestamp: any): Date => {
    if (!timestamp) {
      return new Date();
    }
    // If already a Date object, return it
    if (timestamp instanceof Date) {
      return timestamp;
    }
    // If it has a toDate method (Firestore Timestamp)
    if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    // If it's a string, try to parse it
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    // If it's a number (Unix timestamp in milliseconds)
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    // If it has seconds property (Firestore timestamp object)
    if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
    // Fallback
    return new Date();
  },
};

